import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { uploadExamSessionPhoto } from "../../../services/examSessionsApi";

const ExamPhotoCapture = ({ examId, studentNumber, initialSequenceNumber = 0 }) => {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState("");
  // Continue the server-side counter on resume. Restarting at 1 after a refresh made every photo
  // of the resumed sitting a "repeated sequence number" — logged as suspicious against the student.
  const [sequenceNumber, setSequenceNumber] = useState(
    initialSequenceNumber > 0 ? initialSequenceNumber + 1 : 1
  );
  const [lastUpload, setLastUpload] = useState("");

  useEffect(() => {
    if (!examId || !studentNumber) return;

    let cancelled = false;

    const start = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch (e) {
        setError(t("pages.examSession.cameraError"));
      }
    };

    start();

    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, [examId, studentNumber]);

  const handleCapture = useCallback(async () => {
    if (!videoRef.current || !examId || !studentNumber) return;

    const video = videoRef.current;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const dataUrl = canvas.toDataURL("image/jpeg");
    const timestamp = new Date().toLocaleString().replace("Z", "").split(".")[0];
    const currentSeq = sequenceNumber;

    setError("");
    setLastUpload("");

    try {
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      const file = new File([blob], "p.jpg", { type: "image/jpeg" });

      const response = await uploadExamSessionPhoto({
        examId,
        studentNumber,
        timestamp,
        sequenceNumber: currentSeq,
        file,
      });

      setSequenceNumber(currentSeq + 1);
      setLastUpload(response?.message || t("pages.examSession.lastPhotoSent", { time: new Date().toLocaleTimeString() }));
    } catch (e) {
      const msg = e?.response?.data?.message || t("pages.examSession.photoSendError");
      setError(msg);
      setLastUpload("");
    }
  }, [examId, studentNumber, sequenceNumber, t]);

  useEffect(() => {
    if (!examId || !studentNumber) return;

    let timeoutId;

    const scheduleNextCapture = () => {
      const randomDelay = Math.floor(Math.random() * (80 - 5 + 1) + 5) * 1000;

      timeoutId = window.setTimeout(async () => {
        await handleCapture();
        scheduleNextCapture();
      }, randomDelay);
    };

    scheduleNextCapture();

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [examId, studentNumber, handleCapture]);

  if (!studentNumber) return null;

  return <video ref={videoRef} autoPlay playsInline style={{ display: "none" }} />;
};

export default ExamPhotoCapture;
